import { useState, useCallback } from 'react';

/**
 * Hook for updating hiring offers
 * @param {Object} options - Configuration options
 * @param {number} options.hiringId - Hiring ID
 * @param {string} options.startDate - Start date (optional)
 * @param {string} options.endDate - End date (optional)
 * @param {Function} options.onSuccess - Optional callback called after successful update (receives result data)
 * @param {Function} options.onError - Optional callback called on error (receives error message)
 * @returns {Object} Update state and handler function
 */
const useUpdateHiring = ({ 
  hiringId,
  startDate,
  endDate,
  onSuccess,
  onError
}) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState(null);

  const handleUpdate = useCallback(async () => {
    if (!hiringId) {
      const errorMsg = 'No se puede actualizar: falta el ID de la contratación';
      setError(errorMsg);
      if (onError) onError(errorMsg);
      return { success: false, error: errorMsg };
    }

    setIsUpdating(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_STAGING_URL}/api/hirings/${hiringId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          startDate: startDate,
          endDate: endDate
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const result = { success: true, data };
        if (onSuccess) onSuccess(result);
        return result;
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || 'No se pudo actualizar la oferta de contratación';
        setError(errorMessage);
        if (onError) onError(errorMessage);
        return { success: false, error: errorMessage };
      }
    } catch (error) {
      console.error('Error updating hiring offer:', error);
      const errorMessage = 'Error al actualizar la oferta de contratación';
      setError(errorMessage);
      if (onError) onError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsUpdating(false);
    }
  }, [hiringId, startDate, endDate, onSuccess, onError]);

  return {
    handleUpdate,
    isUpdating,
    error
  };
};

export default useUpdateHiring;

